import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export function DishCategoryManager({ categories, onClose, onChanged, lang }) {
  const [rows, setRows] = useState(() =>
    categories.map((c) => ({ ...c, _label: c.label, _editing: false, _saving: false }))
  );
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRows(categories.map((c) => ({ ...c, _label: c.label, _editing: false, _saving: false })));
  }, [categories]);

  const setRow = (idx, patch) =>
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const saveRow = async (idx) => {
    const row = rows[idx];
    const newLab = (row._label || "").trim();
    if (!newLab) {
      toast.error(lang === "es" ? "El nombre no puede estar vacío" : "Name cannot be empty");
      return;
    }
    setRow(idx, { _saving: true });
    try {
      if (!row.id) {
        // promote a "used-only" category into a real doc
        await api.post("/dish-categories", { name: row.name, label: newLab });
      } else {
        await api.put(`/dish-categories/${row.id}`, { label: newLab });
      }
      toast.success(lang === "es" ? "Categoría actualizada" : "Category updated");
      setRow(idx, { _editing: false, _saving: false, label: newLab });
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
      setRow(idx, { _saving: false });
    }
  };

  const removeRow = async (row) => {
    if (!row.id) {
      toast.info(lang === "es"
        ? "Esta categoría aún no está guardada. Renómbrala primero."
        : "This category is not saved yet. Rename it first.");
      return;
    }
    if (!window.confirm(lang === "es"
      ? `¿Borrar "${row.label}"? Los platos pasarán a "otros".`
      : `Delete "${row.label}"? Dishes will be reassigned to "otros".`)) return;
    try {
      await api.delete(`/dish-categories/${row.id}`, { params: { reassign_to: "otros" } });
      toast.success(lang === "es" ? "Categoría eliminada" : "Category deleted");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addNew = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setBusy(true);
    try {
      await api.post("/dish-categories", {
        name: label.toLowerCase().replace(/\s+/g, "_"),
        label,
      });
      toast.success(lang === "es" ? "Categoría añadida" : "Category added");
      setNewLabel("");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="dish-cat-manager">
        <DialogHeader>
          <DialogTitle>
            {lang === "es" ? "Categorías de la carta" : "Menu categories"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-[var(--text-muted)]">
            {lang === "es"
              ? "Renombra, añade o borra categorías. Si borras una en uso, los platos pasan a \"otros\"."
              : "Rename, add or delete categories. Deleting one in use reassigns dishes to \"otros\"."}
          </p>

          <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
            {rows.map((r, idx) => (
              <div
                key={r.id || r.name}
                className="flex items-center gap-2 py-1.5 px-2 rounded border border-[var(--border-color)] bg-[var(--surface)]"
                data-testid={`cat-row-${r.name}`}
              >
                {r._editing ? (
                  <Input
                    value={r._label}
                    onChange={(e) => setRow(idx, { _label: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") saveRow(idx); }}
                    className="h-8 flex-1"
                    autoFocus
                    data-testid={`cat-input-${r.name}`}
                  />
                ) : (
                  <div className="flex-1 text-sm">
                    <span className="font-medium">{r.label}</span>
                    {r.is_default && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                        {lang === "es" ? "Por defecto" : "Default"}
                      </span>
                    )}
                    {!r.id && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-500">
                        {lang === "es" ? "Sin guardar" : "Unsaved"}
                      </span>
                    )}
                  </div>
                )}
                {r._editing ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => saveRow(idx)}
                      disabled={r._saving}
                      className="h-7 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
                      data-testid={`cat-save-${r.name}`}
                    >
                      {r._saving
                        ? (lang === "es" ? "…" : "…")
                        : (lang === "es" ? "Guardar" : "Save")}
                    </Button>
                    <button
                      onClick={() => setRow(idx, { _editing: false, _label: r.label })}
                      className="p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--text-muted)]"
                      data-testid={`cat-cancel-${r.name}`}
                    >
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setRow(idx, { _editing: true })}
                      className="p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]"
                      title={lang === "es" ? "Renombrar" : "Rename"}
                      data-testid={`cat-edit-${r.name}`}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => removeRow(r)}
                      className="p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--status-negative)]"
                      title={lang === "es" ? "Borrar" : "Delete"}
                      data-testid={`cat-delete-${r.name}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-[var(--border-color)]">
            <Input
              placeholder={lang === "es" ? "Nueva categoría (ej. Tapas)" : "New category (e.g. Tapas)"}
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addNew(); }}
              data-testid="cat-new-input"
              className="h-9"
            />
            <Button
              onClick={addNew}
              disabled={busy || !newLabel.trim()}
              className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
              data-testid="cat-new-add"
            >
              <Plus size={14} className="mr-1" />
              {lang === "es" ? "Añadir" : "Add"}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cat-manager-close">
            {lang === "es" ? "Cerrar" : "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

